import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, Download, CheckCircle2, FileText, MapPin, Loader2, ShieldCheck, Mail, MailCheck, BookOpen, Lock } from 'lucide-react';
import axios from 'axios';
import { generateGenericChecklist } from '../utils/executorChecklistGenerator';
import { DOCUMENTS } from '../content/executorKit';
import { generateSingleDocPdf, generateFullKitPdf } from '../utils/executorKitPdf';
import KitUnlockModal from '../components/KitUnlockModal';
import { useKitUnlock, isKitSlugFree } from '../hooks/useKitUnlock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { title: 'State-Specific Probate Filing Steps', desc: '11-phase roadmap tailored to your state\'s statutes, deadlines, and forms.' },
  { title: 'Immediate First-Week Items', desc: 'What every executor must handle in the first 7 days — locate the will, order death certificates, secure the property.' },
  { title: 'Asset & Debt Inventory Checklist', desc: 'Categorized inventory templates for bank accounts, real estate, vehicles, retirement, life insurance, and outstanding debts.' },
  { title: 'Heir Notification Tracker', desc: 'Formal-notice timing, certified-mail tracking, and signed-receipt requirements.' },
  { title: 'Court Forms Checklist', desc: 'The specific forms required by your state probate court.' },
  { title: 'Tax & Financial Closures', desc: 'Final 1040, Form 1041, federal estate tax (706), beneficiary updates, account closures.' },
  { title: 'Final Distribution & Estate Closure', desc: 'Signed receipts and releases, final accounting approval, petition for discharge.' },
];

const ProbateChecklistPage = () => {
  const [states, setStates] = useState([]);
  const [selected, setSelected] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Email-by-email capture state
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Kit unlock state
  const { unlocked } = useKitUnlock();
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // {type: 'single'|'full'|'view', slug?}
  const [pendingDocTitle, setPendingDocTitle] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/states`).then((r) => {
      if (!cancelled) setStates(r.data || []);
    }).catch((err) => {
      // Universal version still works without states
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Could not load states list; checklist will use universal mode:', err);
      }
    });
    return () => { cancelled = true; };
    // Effect runs only on mount — `API` is a stable module-level constant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async () => {
    setDownloading(true);
    let stateData = null;
    if (selected) {
      try {
        const r = await axios.get(`${API}/states/${selected}`);
        stateData = r.data;
      } catch (err) {
        // Fall back to universal version
        if (process.env.NODE_ENV !== 'production') {
          console.warn('State-specific guidance fetch failed; using universal checklist:', err);
        }
      }
    }
    generateGenericChecklist({ stateData });
    setDownloading(false);
  };

  const sendByEmail = async (e) => {
    e?.preventDefault?.();
    setEmailError('');
    if (!email.trim()) {
      setEmailError('Please enter your email');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads/checklist`, {
        email: email.trim(),
        state_code: selected || null,
        first_name: firstName.trim() || null,
      });
      setSent(true);
      // Still trigger the PDF download so the user has it instantly too
      download();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not send email. Please try downloading the PDF directly.';
      setEmailError(msg);
    } finally {
      setSending(false);
    }
  };

  const requestGatedAction = (action, docTitle = '') => {
    if (unlocked) {
      runAction(action);
    } else {
      setPendingAction(action);
      setPendingDocTitle(docTitle);
      setUnlockModalOpen(true);
    }
  };

  const runAction = (action) => {
    if (!action) return;
    if (action.type === 'single' && action.slug) generateSingleDocPdf(action.slug);
    else if (action.type === 'full') generateFullKitPdf();
  };

  const handleUnlocked = () => {
    if (pendingAction) runAction(pendingAction);
    setPendingAction(null);
    setPendingDocTitle('');
  };

  return (
    <div className="min-h-screen bg-ep-background" data-testid="probate-checklist-page">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-16">
          <div className="container-custom">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-3">
                <span className="text-sm font-medium text-ep-secondary tracking-wide uppercase mb-4 block">
                  Free Resource · No Sign-up Required
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-ep-text mb-6">
                  The Executor's <span className="italic text-ep-primary">Probate Checklist.</span>
                </h1>
                <p className="text-xl text-ep-text-secondary mb-8">
                  A complete, printable workbook covering every step of probate —
                  from locating the will to filing the final accounting.
                </p>

                <div className="bg-white p-6 rounded-2xl border border-ep-border/40 shadow-soft max-w-lg">
                  <label className="text-sm font-medium text-ep-text mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-ep-primary" />
                    Optional: tailor it to your state
                  </label>
                  <Select value={selected} onValueChange={setSelected}>
                    <SelectTrigger className="h-11 mb-4" data-testid="checklist-state-select">
                      <SelectValue placeholder="Select your state (optional)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {states.map((s) => (
                        <SelectItem key={s.state_code} value={s.state_code}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={download}
                    disabled={downloading}
                    className="w-full h-12 bg-ep-primary hover:bg-ep-primary-dark text-white font-medium"
                    data-testid="checklist-download-btn"
                  >
                    {downloading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating PDF…</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download free checklist (PDF)</>
                    )}
                  </Button>
                  <p className="text-xs text-ep-text-muted mt-3 text-center">
                    No email required · ~15 pages · Printable & fillable
                  </p>

                  {/* Email-gated lead capture — optional, sits below the no-friction download */}
                  <div className="mt-5 pt-5 border-t border-ep-border/40">
                    {sent ? (
                      <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="checklist-email-sent-state">
                        <MailCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-emerald-900">Check your inbox</p>
                          <p className="text-xs text-emerald-700 mt-0.5">
                            We've sent the checklist to <strong>{email}</strong>. Look for it from Meg at EstateProFiler. Over the next week you'll receive 2 more emails with executor tips — unsubscribe anytime.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-medium text-ep-text mb-2 flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-ep-primary" />
                          Or get it by email + 3 free tips for executors
                        </p>
                        <form onSubmit={sendByEmail} className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              type="text"
                              placeholder="First name (optional)"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              className="h-10 text-sm"
                              data-testid="checklist-first-name-input"
                            />
                            <Input
                              type="email"
                              placeholder="you@example.com"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              className="h-10 text-sm"
                              data-testid="checklist-email-input"
                            />
                          </div>
                          {emailError && (
                            <p className="text-xs text-red-600" data-testid="checklist-email-error">{emailError}</p>
                          )}
                          <Button
                            type="submit"
                            disabled={sending || !email.trim()}
                            variant="outline"
                            className="w-full h-10 border-ep-primary text-ep-primary hover:bg-ep-primary/5 text-sm"
                            data-testid="checklist-email-submit-btn"
                          >
                            {sending ? (
                              <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Sending…</>
                            ) : (
                              <>Email me the checklist + tips</>
                            )}
                          </Button>
                          <p className="text-[11px] text-ep-text-muted text-center">
                            We'll never sell your email. Unsubscribe with one click.
                          </p>
                        </form>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* PDF preview mock */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <div className="absolute -inset-4 bg-ep-primary/5 rounded-3xl -rotate-2"></div>
                  <div className="relative bg-white rounded-2xl shadow-xl border border-ep-border/30 p-8 transform rotate-1">
                    <div className="bg-ep-primary text-white rounded-xl p-6 -mx-2 mb-4">
                      <p className="text-xs uppercase tracking-wide opacity-80">EstateProFiler</p>
                      <h3 className="font-heading text-xl font-bold mt-2">The Executor's Probate Checklist</h3>
                      <p className="italic text-xs text-ep-secondary mt-2">A complete step-by-step guide…</p>
                    </div>
                    <div className="space-y-3">
                      {['Locate and review the will', 'File petition for probate', 'Notify heirs and beneficiaries', 'Obtain Letters Testamentary', 'Publish notice to creditors'].map((it, i) => (
                        <div key={it} className="flex items-start gap-3 text-sm">
                          <div className={`w-4 h-4 rounded border-2 flex-shrink-0 mt-0.5 ${i < 2 ? 'border-ep-primary bg-ep-primary' : 'border-ep-border'}`}>
                            {i < 2 && <CheckCircle2 className="w-3 h-3 text-white -mt-px -ml-px" />}
                          </div>
                          <span className={i < 2 ? 'text-ep-text-muted line-through' : 'text-ep-text'}>{it}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            FREE RESOURCES — everything below the hero and above the app
            section is a free download. Header divider makes the grouping
            unambiguous so visitors don't conflate the guides with the app.
            ═══════════════════════════════════════════════════════════ */}
        <section className="pt-16 pb-4 bg-white border-t border-ep-border/40" data-testid="free-resources-header">
          <div className="container-custom text-center">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-900 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-4">
              <BookOpen className="w-3.5 h-3.5" /> Part 1 · Free resources
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ep-text mb-3">
              Everything on this section is <span className="italic text-ep-primary">yours to keep</span> — no cost.
            </h2>
            <p className="text-ep-text-muted max-w-2xl mx-auto">
              Two free tools: a printable state-tailored probate checklist and a six-guide Executor Success Kit.
              Nothing on this page is part of the paid software.
            </p>
          </div>
        </section>

        {/* What's inside — sections of the free state checklist */}
        <section className="pt-8 pb-16 bg-white">
          <div className="container-custom">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-sm font-medium text-ep-secondary tracking-wide uppercase mb-4 block">
                What's inside the state checklist
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ep-text mb-4">
                Seven thorough sections.{' '}
                <span className="italic text-ep-primary">One executor-ready workbook.</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {SECTIONS.map((s, i) => (
                <div
                  key={s.title}
                  className="bg-ep-subtle rounded-xl p-6 border border-ep-border/30"
                  data-testid={`checklist-section-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-ep-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-ep-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-ep-text mb-2">
                        {s.title}
                      </h3>
                      <p className="text-sm text-ep-text-secondary leading-relaxed">
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <Button className="bg-ep-primary hover:bg-ep-primary-dark text-white px-8 py-6 text-base group">
                  Download the checklist
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Disclaimer — free-resources footer (was mixed with an app upsell that
            confused the two groups; the app pitch now lives in its own section
            further down). */}
        <section className="py-10 bg-white">
          <div className="container-custom">
            <p className="text-xs text-ep-text-muted mt-2 text-center max-w-3xl mx-auto italic" data-testid="checklist-legal">
              These free guides are provided for educational purposes only and are not legal, tax, or financial advice.
              Probate rules vary by state — consult a licensed attorney for guidance on your specific situation.
            </p>
          </div>
        </section>

        {/* Executor Success Kit — 6 free downloadable guides */}
        <section className="bg-ep-background py-20 border-t border-ep-border" data-testid="executor-kit-section">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-900 px-3 py-1 rounded-full text-xs font-medium mb-4">
                <BookOpen className="w-3.5 h-3.5" /> The Executor Success Kit
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-ep-text mb-3">Six free guides every executor needs</h2>
              <p className="text-ep-text-muted max-w-2xl mx-auto">
                The <strong>First 30 Days Checklist</strong> is fully free — no email required.
                Enter your email once to unlock the other five guides and get the complete kit PDF.
              </p>
              <Button
                onClick={() => requestGatedAction({ type: 'full' })}
                className="mt-6 bg-ep-primary text-white"
                data-testid="download-full-kit-btn"
              >
                {unlocked
                  ? <><Download className="w-4 h-4 mr-2" /> Download the complete kit as one PDF</>
                  : <><Lock className="w-4 h-4 mr-2" /> Unlock & download the complete kit</>}
              </Button>
            </div>

            {!unlocked && (
              <div
                className="max-w-4xl mx-auto mb-10 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 shadow-sm"
                data-testid="kit-value-props"
              >
                <div className="flex items-start gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
                    <MailCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl text-ep-text mb-1">Enter your email once — unlock all five gated guides</h3>
                    <p className="text-sm text-ep-text-muted">
                      One email address, and we send you everything below. No credit card, no account, one-click unsubscribe.
                    </p>
                  </div>
                </div>

                <ul className="space-y-3">
                  {DOCUMENTS.filter((d) => !isKitSlugFree(d.slug)).map((doc) => (
                    <li
                      key={doc.slug}
                      className="flex items-start gap-3 pl-1"
                      data-testid={`kit-value-item-${doc.slug}`}
                    >
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-ep-text font-semibold text-sm leading-snug">{doc.title}</p>
                        <p className="text-ep-text-muted text-sm leading-snug">{doc.subtitle}</p>
                      </div>
                    </li>
                  ))}
                  <li className="flex items-start gap-3 pl-1 pt-3 border-t border-emerald-100" data-testid="kit-value-item-full-kit">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-ep-text font-semibold text-sm leading-snug">The complete kit as a single PDF</p>
                      <p className="text-ep-text-muted text-sm leading-snug">
                        All six guides combined into one downloadable, printable document.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3 pl-1" data-testid="kit-value-item-drip">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-ep-text font-semibold text-sm leading-snug">A short follow-up series with practical executor tips</p>
                      <p className="text-ep-text-muted text-sm leading-snug">
                        Five short emails over the next few weeks. Skip any, or unsubscribe with one click.
                      </p>
                    </div>
                  </li>
                </ul>

                <div className="mt-6 text-center">
                  <Button
                    onClick={() => requestGatedAction({ type: 'full' })}
                    size="lg"
                    className="bg-ep-primary text-white hover:bg-ep-primary-dark px-8"
                    data-testid="kit-value-cta"
                  >
                    <Lock className="w-4 h-4 mr-2" /> Unlock all five guides + the full kit
                  </Button>
                  <p className="text-xs text-ep-text-muted mt-3">Takes 15 seconds. Just your email.</p>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {DOCUMENTS.map((doc) => {
                const free = isKitSlugFree(doc.slug);
                const canDownload = free || unlocked;
                return (
                  <div
                    key={doc.slug}
                    className="bg-white rounded-xl border border-ep-border p-5 flex flex-col relative"
                    data-testid={`kit-card-${doc.slug}`}
                  >
                    {free && (
                      <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                        Free
                      </span>
                    )}
                    {!free && !unlocked && (
                      <span
                        className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                        data-testid={`kit-lock-badge-${doc.slug}`}
                      >
                        <Lock className="w-3 h-3" /> Email required
                      </span>
                    )}
                    <FileText className="w-5 h-5 text-ep-primary mb-3" />
                    <h3 className="font-heading text-lg text-ep-text mb-1">{doc.title}</h3>
                    <p className="text-sm text-ep-text-muted italic mb-4 flex-1">{doc.subtitle}</p>
                    <div className="flex gap-2 mt-auto">
                      {free ? (
                        <Link to={`/executor-kit/${doc.slug}`} className="flex-1">
                          <Button variant="outline" className="w-full text-xs" data-testid={`kit-view-${doc.slug}`}>
                            View online
                          </Button>
                        </Link>
                      ) : (
                        <Link to={`/executor-kit/${doc.slug}`} className="flex-1">
                          <Button variant="outline" className="w-full text-xs" data-testid={`kit-view-${doc.slug}`}>
                            {unlocked ? 'View online' : 'Preview'}
                          </Button>
                        </Link>
                      )}
                      {canDownload ? (
                        <Button
                          onClick={() => generateSingleDocPdf(doc.slug)}
                          variant="outline"
                          className="text-xs"
                          data-testid={`kit-download-${doc.slug}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </Button>
                      ) : (
                        <Button
                          onClick={() => requestGatedAction({ type: 'single', slug: doc.slug }, doc.title)}
                          variant="outline"
                          className="text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                          data-testid={`kit-unlock-${doc.slug}`}
                        >
                          <Lock className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-ep-text-muted mt-8 text-center italic max-w-3xl mx-auto">
              These guides are provided for general informational purposes only. They are not legal, tax, or financial advice.
              Estate rules vary by state — always consult a licensed professional for guidance on your specific situation.
            </p>
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            PART 2 — The paid software. Kept visually distinct (dark
            background, "Part 2" tag, its own feature list) so nobody
            confuses the app with the free resources above.
            ═══════════════════════════════════════════════════════════ */}
        <section className="py-24 bg-ep-primary text-white" data-testid="app-features-section">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-2 bg-white/15 text-white px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-5">
                <ShieldCheck className="w-3.5 h-3.5" /> Part 2 · The software
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-5 leading-tight">
                Ready for something more than a PDF?
              </h2>
              <p className="text-lg text-white/85">
                EstateProFiler takes everything from the free guides above and turns it into
                a working estate-administration platform. Track every asset, deadline, receipt,
                and heir communication in one place — with state-specific tasks and court-ready
                reports built in.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-12">
              {[
                { title: 'State-specific probate tasks', desc: 'Court forms, deadlines, and filing rules for all 50 states applied automatically.' },
                { title: 'Full asset inventory', desc: 'Bank accounts, real estate, retirement, vehicles, digital — one running total that updates as you add.' },
                { title: 'Court-ready reports', desc: 'Final accounting, inventory report, and creditor schedule generated as one-click PDFs.' },
                { title: 'OCR receipt scanning', desc: 'Snap a photo of any receipt and we extract the vendor, date, and amount to your expense log.' },
                { title: 'Heir portal (read-only)', desc: 'Share progress with beneficiaries via secure magic links — no accounts, no confusion.' },
                { title: 'Deadline reminders', desc: 'Automatic alerts before every state deadline so nothing is filed late.' },
              ].map((f) => (
                <div key={f.title} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/15" data-testid={`app-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <CheckCircle2 className="w-5 h-5 text-emerald-300 mb-3" />
                  <h3 className="font-heading text-base font-semibold mb-1.5">{f.title}</h3>
                  <p className="text-sm text-white/80 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <div className="inline-flex flex-col sm:flex-row gap-3 items-center">
                <Link to="/register">
                  <Button className="bg-white text-ep-primary hover:bg-white/95 h-12 px-8 text-base font-semibold" data-testid="app-cta-register">
                    Start free — no credit card
                  </Button>
                </Link>
                <Link to="/features">
                  <Button variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-12 px-6 text-base" data-testid="app-cta-tour">
                    See the full feature tour
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-white/60 mt-4">14-day free trial · Cancel anytime · $29/mo Starter</p>
            </div>
          </div>
        </section>
      </main>

      <KitUnlockModal
        open={unlockModalOpen}
        onOpenChange={setUnlockModalOpen}
        docTitle={pendingDocTitle}
        onUnlocked={handleUnlocked}
      />

      <Footer />
    </div>
  );
};

export default ProbateChecklistPage;
